
import { useState, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Assessment } from '@/types/assessment';
import { getAssessments } from '@/services/assessment'; 
import { archiveAssessment, restoreArchivedAssessment } from '@/services/assessment';
import { useToast } from '@/hooks/use-toast';

export function useAssessments() {
  const [searchTerm, setSearchTerm] = useState('');
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: assessments = [], isLoading: loading } = useQuery({
    queryKey: ['assessments'],
    queryFn: getAssessments,
  });

  const filteredAssessments = useMemo(() => {
    if (searchTerm.trim() === '') return assessments;
    return assessments.filter(assessment => 
      assessment.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      assessment.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm, assessments]);
  
  const handleArchive = async (id: string) => {
    try {
      await archiveAssessment(id);
      queryClient.setQueryData<Assessment[]>(['assessments'], (old) =>
        (old || []).map(a => a.id === id ? { ...a, isActive: false } : a)
      );
      toast({
        title: "Assessment Archived",
        description: "The assessment has been archived successfully."
      });
    } catch (error) {
      console.error("Error archiving assessment:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to archive assessment. Please try again."
      });
    }
  };

  const handleUnarchive = async (id: string) => {
    try {
      await restoreArchivedAssessment(id);
      queryClient.setQueryData<Assessment[]>(['assessments'], (old) =>
        (old || []).map(a => a.id === id ? { ...a, isActive: true } : a)
      );
      toast({
        title: "Assessment Restored",
        description: "The assessment has been restored successfully."
      });
    } catch (error) {
      console.error("Error restoring assessment:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to restore assessment. Please try again."
      });
    }
  };

  return {
    assessments: filteredAssessments,
    loading,
    searchTerm,
    setSearchTerm,
    handleArchive,
    handleUnarchive
  };
}
